// src/pages/StudentRecords.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';
import { format } from 'date-fns';

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const StudentRecords = () => {
    const [regNumber, setRegNumber] = useState('');
    const [student, setStudent] = useState(null);
    const [balances, setBalances] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [searchParams, setSearchParams] = useState({
        term: '',
        year: '',
        paynow_reference: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (regNumber) {
            fetchStudentRecords();
        }
    }, [regNumber]);

    const fetchStudentRecords = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${BASE_URL}/payments/student/${regNumber}`);
            setStudent(response.data.student);
            setBalances(response.data.balances);
            setTransactions(response.data.transactions);
        } catch (err) {
            console.error('Error fetching student records:', err);
            setError('Failed to fetch student records.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prevParams => ({
            ...prevParams,
            [name]: value
        }));
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/payments/student/${regNumber}/search-transactions`, {
                params: searchParams
            });
            setTransactions(response.data.transactions);
        } catch (err) {
            console.error('Error searching transactions:', err);
            setError('Failed to search transactions.');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (transaction) => {
        setSelectedTransaction(transaction);
        setShowModal(true);
    };

    const closeModal = () => {
        setSelectedTransaction(null);
        setShowModal(false);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Student Records</h1>
                        <div className="h-1 w-full bg-gray-200 mt-2"></div>
                    </div>

                    {/* Registration Number Input and Search Button */}
                    <div className="mb-6">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <input
                                type="text"
                                value={regNumber}
                                onChange={(e) => setRegNumber(e.target.value)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                placeholder="Enter registration number"
                            />
                            <button
                                onClick={fetchStudentRecords}
                                className="px-4 py-2 text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                            >
                                Search
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                                    <p className="text-red-700">{error}</p>
                                </div>
                            )}

                            {student && (
                                <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Student Information</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">Reg Number</p>
                                            <p className="text-gray-900">{student.RegNumber}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Name</p>
                                            <p className="text-gray-900">{student.Name} {student.Surname}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Medical Aid</p>
                                            <p className="text-gray-900">{student.HasMedicalAid ? 'Yes' : 'No'}</p>
                                        </div>
                                        {balances.length > 0 && balances.map(balance => (
                                            <React.Fragment key={balance.id}>
                                                <div>
                                                    <p className="text-sm text-gray-500">Term</p>
                                                    <p className="text-gray-900">{balance.term}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Year</p>
                                                    <p className="text-gray-900">{balance.year}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Balance</p>
                                                    <p className="text-gray-900">{balance.balance} ({balance.balance_type})</p>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-800 mb-4">Search Transactions</h2>
                                <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                                        <select
                                            name="term"
                                            value={searchParams.term}
                                            onChange={handleSearchChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        >
                                            <option value="">All Terms</option>
                                            <option value="1">Term 1</option>
                                            <option value="2">Term 2</option>
                                            <option value="3">Term 3</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                        <input
                                            type="number"
                                            name="year"
                                            value={searchParams.year}
                                            onChange={handleSearchChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                            placeholder="e.g., 2024"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Txn Reference</label>
                                        <input
                                            type="text"
                                            name="paynow_reference"
                                            value={searchParams.paynow_reference}
                                            onChange={handleSearchChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                            placeholder="Reference"
                                        />
                                    </div>
                                    <div className="col-span-2 flex items-end">
                                        <button
                                            type="submit"
                                            className="w-full px-4 py-2 text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                        >
                                            Search Transactions
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {transactions.length > 0 ? (
                                <div className="overflow-x-auto rounded-lg border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-pink-600 rounded-t-lg">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tl-lg">Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Txn Ref</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Received Amount</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Reported Amount</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Currency</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tr-lg">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {transactions.map(transaction => (
                                                <tr key={transaction.id} className="hover:bg-pink-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format(new Date(transaction.created_at), 'yyyy-MM-dd HH:mm')}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.paynow_reference}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.received_amount}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.reported_amount}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.currency}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => openModal(transaction)}
                                                            className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                                        >
                                                            View Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : !loading && (
                                <div className="text-center py-12">
                                    <p className="text-gray-500 text-lg">No transaction records at the moment.</p>
                                </div>
                            )}

                            {/* Modal for Transaction Details */}
                            {showModal && selectedTransaction && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                    <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                                        <div className="p-6">
                                            <h3 className="text-lg font-bold text-gray-800 mb-4">Transaction Details</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">Reg Number</p>
                                                    <p className="text-gray-900">{selectedTransaction.reg_number}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Form</p>
                                                    <p className="text-gray-900">{selectedTransaction.form}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Term</p>
                                                    <p className="text-gray-900">{selectedTransaction.term}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Year</p>
                                                    <p className="text-gray-900">{selectedTransaction.year}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Txn Ref</p>
                                                    <p className="text-gray-900">{selectedTransaction.paynow_reference}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Date</p>
                                                    <p className="text-gray-900">{format(new Date(selectedTransaction.created_at), 'yyyy-MM-dd HH:mm')}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Received Amount</p>
                                                    <p className="text-gray-900">{selectedTransaction.received_amount}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Reported Amount</p>
                                                    <p className="text-gray-900">{selectedTransaction.reported_amount}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Currency</p>
                                                    <p className="text-gray-900">{selectedTransaction.currency}</p>
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-sm text-gray-500">Payment Method</p>
                                                    <p className="text-gray-900">{selectedTransaction.payment_method}</p>
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-sm text-gray-500">Status</p>
                                                    <p className="text-gray-900">{selectedTransaction.status}</p>
                                                </div>
                                            </div>
                                            <div className="mt-6 flex justify-end">
                                                <button
                                                    onClick={closeModal}
                                                    className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentRecords;
